import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import siteMessage from "@insite/client-framework/SiteMessage";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LinkProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    title = "title",
    description = "description",
    placeholder = "placeholder",
    disclaimer = "disclaimer",
    alignment = "alignment",
    downloadbutton="downloadbutton",
    downloadbuttonlabel="downloadbuttonlabel"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.label]: string;
        [fields.description]: string;
        [fields.placeholder]: string;
        [fields.disclaimer]: string;
        [fields.alignment]: string;
        [fields.downloadbutton]: string;
        [fields.downloadbuttonlabel]: string;
    };
}

const mapDispatchToProps = {
    subscribe,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SubscribeStyles {
    subscribeWrapper?: InjectableCss;
    titleLabel?: TypographyPresentationProps;
    descriptionText?: TypographyPresentationProps;
    emailFormField?: InjectableCss;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
    disclaimerText?: TypographyPresentationProps;
}

export const subscribeStyles: SubscribeStyles = {
    subscribeWrapper: {
        css: css`
            background: linear-gradient(90deg, #266B40 27.08%, #2D804C 71.87%, #43687A 100%);
            box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
            padding: 40px 105px 48px;
            margin-bottom: 104px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 56px;
                            padding: 25px;
                        `,
                        css`
                            margin-bottom: 56px;
                        `,
                        null,
                    ],
                );
            }}
        `,
    },
    titleLabel: {
        css: css`
            margin-bottom: 16px !important;
            color: ${COLORS.white};
        `,
    },
    descriptionText: {
        css: css`
            margin-bottom: 24px !important;
            line-height: 24px;
            color: ${COLORS.white};

            p {
                line-height: 24px;
            }
        `,
    },
    emailFormField: {
        css: css`
            display: flex;
            input {
                height: 48px !important;
                border-radius: 40px !important;
                padding: 12px 24px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                    display: flex;
                    flex-direction:column;
                    width:auto;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
            `,
        },
    },
    emailButton: {
        mergeCss: true,
        css: css`
            height: 48px !important;
            padding: 12px 24px !important;

            span {
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.large};
                line-height: 24px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                height:100%;
                width:150px !important;
                margin:auto;
                margin-top:8px !important;
                `,
               null,
               null,
               null,
               null,
            ])}
        `,
    },
    disclaimerText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
        `,
    },

   
};

const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.(?!usa$)\\w+([-.]\\w+)*", "i");
export const CmsSubscribe = ({ fields, subscribe, id }: Props) => {
    const emailRequiredFieldMessage = siteMessage("EmailSubscription_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("EmailSubscription_EmailIsInvalidErrorMessage");

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const styles = useMergeStyles("subscribe", subscribeStyles);

    const onSubscribeClick = () => {
        setIsSubmitted(true);
        if (!validateEmail(email)) {
            return;
        }

        subscribe({
            email,
            onSuccess: () => {
                toasterContext.addToast({ body: siteMessage("Email_Subscribe_Success"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    return (
        <StyledWrapper {...styles.subscribeWrapper}>
            <StyledWrapper>
                <Typography variant="h3" {...styles.titleLabel}>{fields.title}</Typography>
            </StyledWrapper>
            <StyledWrapper>
                <Typography variant="p" {...styles.descriptionText}>{parse(transformContentHtml(fields.description), parserOptions)}</Typography>
            </StyledWrapper>
            
            <StyledWrapper {...styles.emailFormField}>
                <TextField
                    {...styles.emailTextField}
                    id={id}
                    placeholder={fields.placeholder}
                    value={email}
                    onChange={e => emailChangeHandler(e.currentTarget.value)}
                    error={isSubmitted && emailError}
                />
                <Button variant="tertiary" {...styles.emailButton} onClick={onSubscribeClick} disabled={isSubmitted && !!emailError}>
                    {fields.label}
                </Button>
                
            </StyledWrapper>
            <StyledWrapper>
                <Typography {...styles.disclaimerText}>{parse(transformContentHtml(fields.disclaimer), parserOptions)}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CmsSubscribe),
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            {
                name: fields.alignment,
                displayName: "Layout",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "Translatable",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
                fieldType: "Translatable",
            },
            {
                name: fields.placeholder,
                displayName: "Placeholder Text",
                editorTemplate: "TextField",
                defaultValue: "Enter email address",
                fieldType: "Translatable",
            },
            {
                name: fields.disclaimer,
                displayName: "Disclaimer",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
